/**
 * Separate React entry point for the Facilitator Portal.
 *
 * This mounts a standalone app with its own router — no admin
 * dashboard code is imported. When deploying to facilitator.playbook.plus,
 * serve facilitator.html instead of index.html.
 */
import React, { Component, type ReactNode, type ErrorInfo } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import FacilitatorPortal from './pages/facilitator-portal/FacilitatorPortal';
import { I18nProvider } from './lib/I18nProvider';
import './styles/globals.css';
import { initTelemetry, captureException } from './lib/telemetry';
import { reloadForNewVersion } from './lib/staleBuild';

// Error reports (error-alerts-silent): off unless a Sentry DSN is built in.
void initTelemetry();

// A screen file from an older build is gone after an update: reload once for
// the new version (stale-tab-after-update, 3 Oct). See lib/staleBuild.ts.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadForNewVersion()) event.preventDefault();
});

// F3 (2026-06-12) — belt-and-braces dark mode. The class also lives on
// <html> in index.html, but Vite dev sometimes serves a stale HTML
// shell (and Cloudflare Pages can serve a stale build). Applying it
// from JS too guarantees the class is present in every environment.
if (typeof document !== 'undefined') {
  document.documentElement.classList.add('dark');
  document.documentElement.style.colorScheme = 'dark';
}

// Fuad #4 (2026-06-12) — fix blank-page on production builds.
// vite.config.ts has a `configureServer` plugin that 302-redirects
// `/` → `/facilitator/` in dev. That hook does NOT run in `vite
// preview` or in Docker static-serving — production builds were
// serving the SPA shell at `/` to a BrowserRouter with basename
// `/facilitator`, so no route matched and the page rendered blank.
// Mirror the redirect client-side as a runtime guard. Costs one
// immediate replace() on cold load when path is root.
if (typeof window !== 'undefined') {
  const path = window.location.pathname;
  if (path === '/' || path === '/index.html') {
    window.location.replace('/facilitator/');
  }
}

// ─── Top-level error boundary — catches ANY React error ─────────────────────

class RootErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[FacilitatorPortal ROOT crash]', error, info.componentStack);
    captureException(error);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, fontFamily: 'system-ui, sans-serif', background: '#111', color: '#f88', minHeight: '100vh' }}>
          <h1 style={{ fontSize: 20, marginBottom: 16 }}>Playbook Studio — Crash</h1>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, background: '#1a1a1a', padding: 16, borderRadius: 8, color: '#ccc' }}>
            {this.state.error.message}{'\n\n'}{this.state.error.stack}
          </pre>
          <button
            onClick={() => {
              localStorage.removeItem('playbook_facilitator_portal');
              window.location.reload();
            }}
            style={{ marginTop: 16, padding: '8px 16px', background: '#4466ff', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
          >
            Clear session &amp; reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── App setup ──────────────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// main.tsx is the app entry — fast refresh doesn't apply here because
// this file is never a hot-reload target (it only runs at bootstrap).
// The lint rule doesn't know that, so silence it locally.
// eslint-disable-next-line react-refresh/only-export-components
function FacilitatorApp() {
  return (
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <I18nProvider>
          <BrowserRouter basename="/facilitator">
            <Routes>
              <Route path="/*" element={<FacilitatorPortal />} />
            </Routes>
          </BrowserRouter>
        </I18nProvider>
      </QueryClientProvider>
    </RootErrorBoundary>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <FacilitatorApp />
  </React.StrictMode>,
);
